import React, { useEffect, useMemo, useState } from 'react';
import { Globe, Plus, Trash2, Key, Lock, QrCode } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '@/contexts/AuthContext';
import { listQRCampaigns, type QRCampaign } from '@/lib/qr-api-client';

interface WebhookStepConfigProps {
  config: any;
  onChange: (config: any) => void;
}

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

const AUTH_TYPES = [
  { value: 'none', label: 'No Authentication' },
  { value: 'basic', label: 'Basic Auth' },
  { value: 'bearer', label: 'Bearer Token' },
  { value: 'api_key', label: 'API Key' },
];

const CONTENT_TYPES = [
  { value: 'application/json', label: 'JSON' },
  { value: 'application/x-www-form-urlencoded', label: 'Form Data' },
  { value: 'application/xml', label: 'XML' },
  { value: 'text/plain', label: 'Plain Text' },
];

export const WebhookStepConfig: React.FC<WebhookStepConfigProps> = ({ config, onChange }) => {
  const { marketingUser } = useAuth();
  const companyId = useMemo(() => {
    const ids: string[] = marketingUser?.company_ids || [];
    return ids[0] || 'halesac';
  }, [marketingUser]);

  const [qrCampaigns, setQrCampaigns] = useState<QRCampaign[]>([]);

  const headers = config.headers || [];
  const authType = config.auth_type || 'none';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await listQRCampaigns(companyId);
        if (!cancelled) setQrCampaigns(data);
      } catch (e: any) {
        if (!cancelled) {
          console.error(e);
          // Keep quiet in UI; only toast once per open.
          toast.error(e?.message || 'Failed to load QR campaigns');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const addHeader = () => {
    onChange({
      ...config,
      headers: [...headers, { key: '', value: '' }],
    });
  };

  const removeHeader = (index: number) => {
    const newHeaders = headers.filter((_: any, i: number) => i !== index);
    onChange({
      ...config,
      headers: newHeaders,
    });
  };

  const updateHeader = (index: number, field: 'key' | 'value', value: string) => {
    const newHeaders = [...headers];
    newHeaders[index] = { ...newHeaders[index], [field]: value };
    onChange({
      ...config,
      headers: newHeaders,
    });
  };

  return (
    <div className="space-y-6">
      {/* Webhook URL */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Webhook URL *
        </label>
        <div className="flex items-center gap-2">
          <Globe className="w-5 h-5 text-gray-400 flex-shrink-0" />
          <input
            type="url"
            value={config.url || ''}
            onChange={(e) => onChange({ ...config, url: e.target.value })}
            placeholder="https://api.example.com/webhook"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

      {/* QR Tracking */}
      <div className="border rounded-lg p-4 bg-white">
        <div className="flex items-center gap-2 mb-2">
          <QrCode className="w-4 h-4 text-gray-600" />
          <h4 className="text-sm font-medium text-gray-700">QR Tracking (optional)</h4>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">QR Campaign</label>
            <select
              value={config.qr_campaign_id || ''}
              onChange={(e) => onChange({ ...config, qr_campaign_id: e.target.value || undefined })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">None</option>
              {qrCampaigns
                .filter((c) => c.company_id === companyId)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              If set, the workflow engine will generate/reuse a per-recipient QR code and expose merge tags.
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Include QR Merge Tags</label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={config.include_qr !== false}
                onChange={(e) => onChange({ ...config, include_qr: e.target.checked })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                disabled={!config.qr_campaign_id}
              />
              <span className="ml-2 text-sm text-gray-700">Enable QR tags in templates</span>
            </label>
            <div className="mt-2 text-xs text-gray-600 font-mono space-y-1">
              <div>{`{{qr_tracking_url}}`}</div>
              <div>{`{{qr_image_url}}`}</div>
              <div>{`{{qr_short_code}}`}</div>
            </div>
          </div>
        </div>
      </div>
        <p className="mt-1 text-xs text-gray-500">
          The endpoint that will receive the webhook request
        </p>
      </div>

      {/* HTTP Method */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          HTTP Method *
        </label>
        <div className="flex gap-2">
          {HTTP_METHODS.map(method => (
            <label
              key={method}
              className={`flex-1 flex items-center justify-center px-3 py-2 border rounded-lg cursor-pointer transition-colors ${
                config.method === method
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-300 hover:bg-gray-50'
              }`}
            >
              <input
                type="radio"
                name="method"
                value={method}
                checked={config.method === method}
                onChange={(e) => onChange({ ...config, method: e.target.value })}
                className="sr-only"
              />
              <span className="font-medium text-sm">{method}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Content Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Content Type
        </label>
        <select
          value={config.content_type || 'application/json'}
          onChange={(e) => onChange({ ...config, content_type: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          {CONTENT_TYPES.map(type => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
      </div>

      {/* Request Body */}
      {(config.method === 'POST' || config.method === 'PUT' || config.method === 'PATCH') && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Request Body (JSON)
          </label>
          <textarea
            value={config.body || ''}
            onChange={(e) => onChange({ ...config, body: e.target.value })}
            placeholder={'{\n  "contact_id": "{{contact_id}}",\n  "email": "{{email}}",\n  "status": "enrolled"\n}'}
            rows={6}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
          />
          <p className="mt-1 text-xs text-gray-500">
            Use merge tags like {`{{contact_id}}`}, {`{{email}}`}, {`{{first_name}}`}, etc.
          </p>
        </div>
      )}

      {/* Custom Headers */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium text-gray-700">
            Custom Headers
          </label>
          <button
            type="button"
            onClick={addHeader}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"
          >
            <Plus className="w-4 h-4" />
            Add Header
          </button>
        </div>

        {headers.length > 0 ? (
          <div className="space-y-2">
            {headers.map((header: any, index: number) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="text"
                  value={header.key || ''}
                  onChange={(e) => updateHeader(index, 'key', e.target.value)}
                  placeholder="Header name"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                <input
                  type="text"
                  value={header.value || ''}
                  onChange={(e) => updateHeader(index, 'value', e.target.value)}
                  placeholder="Header value"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                <button
                  type="button"
                  onClick={() => removeHeader(index)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                  title="Remove header"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 italic">No custom headers added</p>
        )}
      </div>

      {/* Authentication */}
      <div className="border-t pt-6">
        <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
          <Lock className="w-4 h-4" />
          Authentication
        </h4>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Authentication Type
          </label>
          <select
            value={authType}
            onChange={(e) => onChange({ ...config, auth_type: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {AUTH_TYPES.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        {/* Basic Auth */}
        {authType === 'basic' && (
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Username
              </label>
              <input
                type="text"
                value={config.auth_username || ''}
                onChange={(e) => onChange({ ...config, auth_username: e.target.value })}
                placeholder="Enter username"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Password
              </label>
              <input
                type="password"
                value={config.auth_password || ''}
                onChange={(e) => onChange({ ...config, auth_password: e.target.value })}
                placeholder="Enter password"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        )}

        {/* Bearer Token */}
        {authType === 'bearer' && (
          <div className="mt-4">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Bearer Token
            </label>
            <input
              type="password"
              value={config.auth_token || ''}
              onChange={(e) => onChange({ ...config, auth_token: e.target.value })}
              placeholder="Enter bearer token"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        )}

        {/* API Key */}
        {authType === 'api_key' && (
          <div className="mt-4 space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                API Key Location
              </label>
              <select
                value={config.api_key_location || 'header'}
                onChange={(e) => onChange({ ...config, api_key_location: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="header">Header</option>
                <option value="query">Query Parameter</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                API Key Name
              </label>
              <input
                type="text"
                value={config.api_key_name || ''}
                onChange={(e) => onChange({ ...config, api_key_name: e.target.value })}
                placeholder="e.g., X-API-Key or api_key"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                API Key Value
              </label>
              <input
                type="password"
                value={config.api_key_value || ''}
                onChange={(e) => onChange({ ...config, api_key_value: e.target.value })}
                placeholder="Enter API key"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        )}
      </div>

      {/* Retry & Timeout Settings */}
      <div className="border-t pt-6">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Retry & Timeout Settings</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Timeout (seconds)
            </label>
            <input
              type="number"
              value={config.timeout || 30}
              onChange={(e) => onChange({ ...config, timeout: parseInt(e.target.value) })}
              min="1"
              max="300"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Max Retries
            </label>
            <input
              type="number"
              value={config.max_retries || 3}
              onChange={(e) => onChange({ ...config, max_retries: parseInt(e.target.value) })}
              min="0"
              max="10"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Request will timeout after the specified seconds. Failed requests will be retried up to the max retry count.
        </p>
      </div>

      {/* Response Handling */}
      <div className="space-y-3">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.save_response || false}
            onChange={(e) => onChange({ ...config, save_response: e.target.checked })}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="ml-2 text-sm text-gray-700">Save response data</span>
        </label>
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.continue_on_error || false}
            onChange={(e) => onChange({ ...config, continue_on_error: e.target.checked })}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="ml-2 text-sm text-gray-700">Continue workflow even if webhook fails</span>
        </label>
      </div>

      {/* Success Criteria */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Success Status Codes
        </label>
        <input
          type="text"
          value={config.success_codes || '200,201,202,204'}
          onChange={(e) => onChange({ ...config, success_codes: e.target.value })}
          placeholder="200,201,202,204"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <p className="mt-1 text-xs text-gray-500">
          Comma-separated list of HTTP status codes considered successful
        </p>
      </div>

      {/* Info Box */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Webhook Best Practices</h4>
        <ul className="text-xs text-gray-600 space-y-1">
          <li>• Always use HTTPS for secure communication</li>
          <li>• Test webhooks with a service like webhook.site before deploying</li>
          <li>• Set appropriate timeout values (typically 30-60 seconds)</li>
          <li>• Use authentication to secure your webhook endpoints</li>
          <li>• Monitor webhook failures and set up alerts</li>
          <li>• Consider implementing idempotency for retry scenarios</li>
        </ul>
      </div>
    </div>
  );
};
